import { inject } from '@angular/core';
import {
  HttpInterceptorFn,
  HttpRequest,
  HttpHandlerFn,
  HttpErrorResponse,
} from '@angular/common/http';
import { Observable, forkJoin, switchMap, catchError, throwError } from 'rxjs';
import { AuthService } from './auth.service';

const GRAPH_BASE = 'https://graph.microsoft.com';
const SHAREPOINT_BASE = 'https://asiandevbank.sharepoint.com';
const YAMMER_BASE = 'https://www.yammer.com';
// APIM path segment that identifies Yammer-proxied endpoints requiring dual auth
const APIM_YAMMER_PATH = '/yammer/';

/**
 * Determines whether the given URL is an APIM Yammer route.
 * These routes need both the standard intranet Bearer token and an x-yammer-token header.
 */
function isApimYammerUrl(url: string): boolean {
  return url.includes(APIM_YAMMER_PATH);
}

// APIM path segment shared by all admin analytics endpoints, which also require a Graph token
const ADMIN_ANALYTICS_PATH = '/analytics/v1/analytics/';

// Current-user endpoint; matched exactly (not a prefix) so e.g. `user/v1/media` is unaffected
const USER_ME_PATH_REGEX = /\/user\/v1\/me(?:[?#]|$)/;

/** Routes that need the intranet Bearer token plus a Graph token as x-graph-token. */
function needsGraphToken(url: string): boolean {
  return url.includes(ADMIN_ANALYTICS_PATH) || USER_ME_PATH_REGEX.test(url);
}

function tokenForUrl(url: string, authService: AuthService): Observable<string> {
  if (url.startsWith(GRAPH_BASE)) {
    return authService.getAccessToken(['User.Read']);
  }
  if (url.startsWith(SHAREPOINT_BASE)) {
    return authService.getSharepointToken();
  }
  if (url.startsWith(YAMMER_BASE)) {
    return authService.getYammerToken();
  }
  return authService.getAccessToken();
}

export const authTokenInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
) => {
  const authService = inject(AuthService);

  // APIM Yammer routes need both the intranet Bearer token and a Yammer token as x-yammer-token
  if (isApimYammerUrl(req.url)) {
    return forkJoin({
      accessToken: authService.getAccessToken(),
      yammerToken: authService.getYammerToken(),
    }).pipe(
      switchMap(({ accessToken, yammerToken }) =>
        next(
          req.clone({
            setHeaders: {
              'x-yammer-token': yammerToken,
              Authorization: `Bearer ${accessToken}`,
            },
          }),
        ),
      ),
      catchError((err: unknown) => {
        return throwError(() => err);
      }),
    );
  }

  // Admin analytics and user/v1/me need the intranet Bearer token plus a Graph token as x-graph-token
  if (needsGraphToken(req.url)) {
    return forkJoin({
      accessToken: authService.getAccessToken(),
      graphToken: authService.getAccessToken(['User.Read']),
    }).pipe(
      switchMap(({ accessToken, graphToken }) =>
        next(
          req.clone({
            setHeaders: {
              'x-graph-token': graphToken,
              Authorization: `Bearer ${accessToken}`,
            },
          }),
        ),
      ),
      catchError((err: unknown) => {
        if (err instanceof HttpErrorResponse && err.status === 401) {
          authService.logout();
        }
        return throwError(() => err);
      }),
    );
  }

  return tokenForUrl(req.url, authService).pipe(
    switchMap((token) => next(req.clone({ setHeaders: { Authorization: `Bearer ${token}` } }))),
    catchError((err: unknown) => {
      if (err instanceof HttpErrorResponse && err.status === 401) {
        authService.logout();
      }
      return throwError(() => err);
    }),
  );
};
